
import { mapGetters, mapActions } from 'vuex';
import { PortfolioOrderTypes } from '@/utils/constants/portfolioConstants';
import { isOrderPending } from '@/utils/orders';
import SearchSymbolWrapper from '@/components/stocks/SearchSymbolWrapper';
import StockOperationCard from '~/components/ui/molecule/StockOperationCard';
import UITickersDropdown from '~/components/ui/organism/UITickersDropdown';
import { debounce } from 'lodash';
import DropDownOrderType from '~/components/ui/molecule/DropDownOrderType.vue';
import { getListedPortfolioEarliestOperationDate } from '@/utils/portfolio-dates/';
import {
  projectedLineRepartitionPercent,
  projectedLineRepartitionPercentRounded,
  exceedsThirtyPercentRule,
} from '@/utils/thirty-percent-rule.ts';
// SKD-2267 - Removing weekly sell limit for now
// import { getCurrentWeekStartUtc } from '@/utils/date-range';
// import { RANKED_PORTFOLIO_WEEKLY_SELL_OPERATION_LIMIT } from '@/utils/constants/portfolioConstants';
import { _operationQuantityMaxDecimals } from '@/utils/portfolio-settings';
import { getEligiblePortfoliosForSymbol } from '~/utils/behavior/portfolio-eligibility';
import { showErrorToast } from '~/utils/toast-notifications';
import { getTodayAsDateString } from '~/utils/date-formatter';
import { getTransactableSymbolSearchTypes } from '~/integrations/api/transactable-symbol-search';

export default {
  name: 'CreatePortfolioAssetOperationModal',
  components: {
    SearchSymbolWrapper,
    StockOperationCard,
    UITickersDropdown,
    DropDownOrderType,
  },
  mixins: [getListedPortfolioEarliestOperationDate],
  props: {
    isCreateOperationLoading: {
      type: Boolean,
      default: false,
    },
    isAddItemLoading: {
      type: Boolean,
      default: false,
    },
    tickerToAdd: {
      type: Object,
      default: () => ({
        low: 0,
        high: 0,
      }),
      validator: (v) => ['low', 'high'].every((k) => k in v),
    },
    portfolios: {
      type: Array,
      default: () => [],
    },
    defaultPortfolio: {
      type: Object,
      default: () => null,
    },
    showTickerSearch: {
      type: Boolean,
      default: false,
    },
    showPortfolioSearch: {
      type: Boolean,
      default: false,
    },
    defaultOperationType: {
      type: String,
      required: true,
      validator: (v) => ['Buy', 'Sell'].includes(v),
    },
    priceLoading: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      required: true,
    },
    cashAvailable: {
      type: Object,
      default: null,
    },
    daysToSubstract: {
      type: Number,
      default: 2,
    },
    transactionError: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'onAddSymbol',
    'onClose',
    'onDateChange',
    'onPortfolioChange',
    'onSubmit',
    'orderValidityChange',
  ],
  data() {
    return {
      error: '',
      price: null,
      quantity: null,
      quantityPercentage: 0,
      minQuantity: 0.000001,
      maxTotal: 10000000,
      maxDate: this.$dayjs()
        .subtract(this.daysToSubstract, 'day')
        .format('YYYY-MM-DD'),
      formValues: {
        operationType: 'Buy',
        portfolioName: '',
        date: this.maxDate, // this.$dayjs().subtract(2, 'day').format('YYYY-MM-DD'),  // this.getMaxDate()
        quantity: 1,
        price: 0,
        chosenCurrency: '', // For STOCK TRANSACTION
        chosenCurrencyCash: '', // For CASH imports
      },
      total: null,
      conversionRate: 1,
      formIsMultiple: false,
      cashAvailableText: {},
      reset: false,
      symbolToSell: null,
      focusTickerSearchField: false,
      activeField: null,
      tickerToPfCurrencyConversion: {
        fetchedRate: null,
        loading: false,
        // lastFrom and lastTo are used to avoid unnecessary API calls when the conversion rate is already fetched for the same currencies
        lastFrom: null,
        lastTo: null,
      },
      orderLimitPrice: null,
      selectedOrderType: PortfolioOrderTypes.MARKET_PRICE,
    };
  },
  computed: {
    ...mapGetters('modals', [
      'createPortfolioOperationModal',
      'showNoDataAtDateError',
    ]),
    // The symbol search should only include symbols that can be transacted (see domain/transactions-behavior.ts).
    transactableSymbolSearchTypes() {
      return getTransactableSymbolSearchTypes();
    },
    convertedProjectedItemMarketValue() {
      if (this.tickerToPfCurrencyConversion.fetchedRate === null) return null;
      const existingVal = this.conversionInputs.existingItemMarketValue;
      if (existingVal === undefined) return null;
      return (
        // For limit order, we want to use the limit price to validate the 30% rule rather than the current stock price
        (this.quantity * this.effectivePrice + existingVal) *
        this.tickerToPfCurrencyConversion.fetchedRate
      );
    },
    tickerToPfCurrencyConversionLoading() {
      return this.tickerToPfCurrencyConversion.loading;
    },
    min() {
      return this.tickerToAdd ? parseFloat(this.tickerToAdd.low.toFixed(2)) : 0;
    },
    max() {
      // TODO: Bug prone. tickerToAdd.high is a float which might be actually
      // "Infinity", so, in reality, typeof TickerToAdd.high === string |
      // number, meaning that you need special care for this. Also poorly
      // internationalized -- see display/index.ts for the function you should
      // use.
      return this.tickerToAdd
        ? parseFloat(this.tickerToAdd.high.toFixed(2))
        : 0;
    },
    lineRepartitionMessage() {
      const key = this.exceedsThirtyPercentRule
        ? 'portfolio_line_repartition_error'
        : 'portfolio_line_repartition_info';
      return this.$t(key, {
        percent: this.projectedLineRepartitionPercentRounded + '%',
      });
    },
    // TODO: Split into a library we inject and call. yesterday is agnostic of
    // the component.
    disabled() {
      if (this.formValues.operationType == 'Sell') {
        return (
          this.error ||
          !this.quantity ||
          this.quantity > this.maxQuantityToSell ||
          (this.min === 0 && this.max === 0) ||
          (!this.defaultPortfolio.leagueId && !this.withinPriceRange())
        );
      }
      return (
        this.error ||
        !this.quantity ||
        (this.min === 0 && this.max === 0) ||
        (!this.defaultPortfolio.leagueId && !this.withinPriceRange()) ||
        this.hasInsufficientCash ||
        this.exceedsThirtyPercentRule
      );
    },
    buttonState() {
      if (this.tickerToPfCurrencyConversionLoading) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      // SKD-2267 - Removing weekly sell limit for now
      // if (
      //   this.formValues.operationType === 'Sell' &&
      //   this.defaultPortfolio.leagueId &&
      //   !this.defaultPortfolio.inUnrankedState &&
      //   this.weeklySellOperationCount >=
      //     this.rankedPortfolioWeeklySellOperationLimit
      // ) {
      //   this.$emit('buttonStateChange', 'disabled');
      //   return 'disabled';
      // }
      if (
        this.formValues.date > this.maxDate ||
        this.formValues.date <= this.minDate
      ) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if ((this.total <= 0 && this.quantity) || this.disabled) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (this.limitPriceNotValid) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (!this.isOrderValid) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (this.isCreateOperationLoading || this.isAddItemLoading) {
        this.$emit('buttonStateChange', 'loading');
        return 'loading';
      }
      if (this.quantity < this.minQuantity && this.operationType == 'Buy') {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (this.total > this.maxTotal) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (this.hasInsufficientCash) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (
        this.exceedsThirtyPercentRule === null &&
        this.defaultPortfolio?.isPublic === true &&
        this.formValues?.operationType === 'Buy'
      ) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      }
      if (this.exceedsThirtyPercentRule) {
        this.$emit('buttonStateChange', 'disabled');
        return 'disabled';
      } else {
        this.$emit('buttonStateChange', 'enabled');
        return 'enabled';
      }
    },
    minDate() {
      const listedPortfolioEarliestDate = this.$dayjs(
        this.getListedPortfolioEarliestOperationDate()
      );
      const minDate = this.defaultPortfolio.isEligibleToLadder
        ? listedPortfolioEarliestDate.subtract(1, 'day').format('YYYY-MM-DD')
        : this.$dayjs().subtract(10, 'years').format('YYYY-MM-DD');
      if (!this.createPortfolioOperationModal?.symbol) {
        return minDate;
      }
      const item = this.defaultPortfolio.items.find(
        (i) => i.symbol == this.createPortfolioOperationModal.symbol.name
      );
      if (!item || !item.operations || item.operations.length == 0) {
        return minDate;
      }
      item.operations.sort((a, b) =>
        this.$dayjs(a.OperationDate).isAfter(this.$dayjs(b.OperationDate))
          ? 1
          : -1
      );
      const lastOperationDate = this.$dayjs(item.operations[0].operationDate)
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      if (this.defaultPortfolio.isEligibleToLadder) {
        return this.$dayjs(minDate).isAfter(lastOperationDate)
          ? minDate
          : lastOperationDate;
      } else {
        return lastOperationDate;
      }
    },
    originalCurrency() {
      if (this.tickerToAdd === null) {
        return '';
      }
      if (
        !(
          'currencySymbol' in this.tickerToAdd || 'currency' in this.tickerToAdd
        )
      ) {
        return '';
      }
      if (this.tickerToAdd.currencySymbol !== null) {
        return this.tickerToAdd.currencySymbol;
      }
      if (this.tickerToAdd.currency !== null) {
        return this.tickerToAdd.currency;
      }
      return '';
    },
    shouldShowTickerSearchComponent() {
      return (
        this.createPortfolioOperationModal.symbol === undefined ||
        this.createPortfolioOperationModal.symbol === null
      );
    },
    shouldSubmit() {
      return this.createPortfolioOperationModal.isSubmitting;
    },
    maxQuantityToSell() {
      let quantity = 0;
      let operations = [];
      let sellOperations = [];
      const formDate = this.$dayjs(this.formValues.date);
      if (this.symbolToSell) {
        operations = this.symbolToSell.operations?.filter?.((o) => {
          return (
            o.operationType == 'Buy' &&
            (this.$dayjs(o.operationDate).isBefore(formDate) ||
              this.$dayjs(o.operationDate).isSame(formDate, 'day'))
          );
        });
        sellOperations = this.symbolToSell.operations?.filter?.((o) => {
          return (
            o.operationType == 'Sell' &&
            (this.$dayjs(o.operationDate).isBefore(formDate) ||
              this.$dayjs(o.operationDate).isSame(formDate, 'day'))
          );
        });
      } else if (this.createPortfolioOperationModal.symbol) {
        operations =
          this.createPortfolioOperationModal.symbol.operations.filter((o) => {
            return (
              o.operationType == 'Buy' &&
              (this.$dayjs(o.operationDate).isBefore(formDate) ||
                this.$dayjs(o.operationDate).isSame(formDate, 'day'))
            );
          });
        sellOperations =
          this.createPortfolioOperationModal.symbol.operations.filter((o) => {
            return (
              o.operationType == 'Sell' &&
              (this.$dayjs(o.operationDate).isBefore(formDate) ||
                this.$dayjs(o.operationDate).isSame(formDate, 'day'))
            );
          });
      }
      if (!operations?.length) {
        return 0;
      }
      quantity += operations?.reduce((n, { quantity }) => n + quantity, 0) ?? 0;
      quantity -=
        sellOperations?.reduce((n, { quantity }) => n + quantity, 0) ?? 0;

      return quantity;
    },
    getErrorForBuy() {
      if (this.total > this.maxTotal) {
        return this.$t('field_operation_total_max');
      } else if (this.hasInsufficientCash) {
        return this.$t('not_enough_cash_error_msg');
      }
      return null;
    },
    getErrorForSell() {
      // SKD-2170 - Removing 0 quantity validation error message
      // if (this.quantity < this.minQuantity) {
      //   return this.$t('field_operation_quantity_min');
      // } else
      if (this.quantity > this.maxQuantityToSell) {
        return this.$t('not_enough_shares');
      } else if (this.total > this.maxTotal) {
        return this.$t('field_operation_total_max');
      }
      return null;
    },
    cashOperationsPermitted() {
      //SKD-2310 - This has been disabled as per product decision, all cash operations are disabled for now
      // putting the disable here for both clarity and to allow easy revert
      return false;
      // If portfolio has no league, cash operations are permitted
      // if (!this.defaultPortfolio.league) {
      //   return true;
      // }

      // return this.defaultPortfolio.league.cashOperationsPermitted;
    },
    hasInsufficientCash() {
      if (
        // NOTE - SKD-2310 - This is now disabled for all portfolios. Visit cashOperationsPermitted() and remove comment if that changes
        this.cashOperationsPermitted ||
        this.formValues.operationType !== 'Buy'
      ) {
        return false;
      }

      const chosenCurrency = this.formValues.chosenCurrency;
      if (
        !chosenCurrency ||
        !this.cashAvailable ||
        !this.cashAvailable[chosenCurrency]
      ) {
        return false;
      }

      const availableCash = this.cashAvailable[chosenCurrency].value;
      return this.total > availableCash;
    },
    filteredPortfolios() {
      if (!this.portfolios) return [];
      if (!this.currentSymbolName) return [];

      return getEligiblePortfoliosForSymbol(
        this.currentSymbolName,
        this.portfolios
      );
    },
    isPortfolioInLeague() {
      return this.defaultPortfolio.leagueId != null;
    },
    // SKD-2267 - Removing weekly sell limit for now
    // rankedPortfolioWeeklySellOperationLimit() {
    //   return (
    //     this.defaultPortfolio?.league?.maxWeeklySellLimit ??
    //     RANKED_PORTFOLIO_WEEKLY_SELL_OPERATION_LIMIT
    //   );
    // },
    // weeklySellOperationCount() {
    //   return this.defaultPortfolio.items
    //     .flatMap((i) => i.operations ?? [])
    //     .filter((item) => {
    //       return (
    //         item.operationType === 'Sell' &&
    //         (getCurrentWeekStartUtc().isBefore(
    //           this.$dayjs(item.operationDate)
    //         ) ||
    //           getCurrentWeekStartUtc().isSame(
    //             this.$dayjs(item.operationDate),
    //             'day'
    //           ))
    //       );
    //     }).length;
    // },
    orderTypes() {
      return PortfolioOrderTypes;
    },
    conversionInputs() {
      const symbolName = this.currentSymbolName;

      // If defaultPortfolio is null/undefined, return undefined - this makes it clear the data hasn't even been loaded
      if (!this.defaultPortfolio) {
        return {
          operationType: this.formValues?.operationType,
          quantity: this.quantity,
          price: this.price,
          from: this.tickerToAdd?.currency,
          to: undefined,
          existingItemMarketValue: undefined,
        };
      }

      // If there is no existing item, return null - this is a valid case for when the user is adding this symbol for the first time
      const existingItem = symbolName
        ? this.defaultPortfolio.items?.find((i) => i.symbol === symbolName)
        : undefined;

      return {
        operationType: this.formValues?.operationType,
        quantity: this.quantity,
        price: this.price,
        from: this.tickerToAdd?.currency,
        to: this.defaultPortfolio.currency,
        existingItemMarketValue: existingItem?.marketValue ?? null,
      };
    },
    projectedLineRepartitionPercent() {
      const isPublic = this.defaultPortfolio?.isPublic;
      const portfolioMarketValue = this.defaultPortfolio?.marketValue;
      if (!isPublic || !portfolioMarketValue) return null;
      if (!this.quantity || !this.price) return null;
      // We don't need to validate nor show the line repartition for sell operations
      if (this.operationType === 'Sell') return null;

      const tickerCurrency = this.tickerToAdd?.currency;
      const portfolioCurrency = this.defaultPortfolio?.currency;
      const existingItemMarketValue =
        this.conversionInputs.existingItemMarketValue;

      if (!tickerCurrency || !portfolioCurrency) {
        console.error(
          'Unable to compute projected line repartition: tickerCurrency or portfolioCurrency is null'
        );
        showErrorToast(this.$t('error_occured'));
        return null;
      }

      let projectedLineMarketValueInPortfolioCurrency;
      if (tickerCurrency === portfolioCurrency) {
        projectedLineMarketValueInPortfolioCurrency =
          // For limit order, we want to use the limit price to validate the 30% rule rather than the current stock price
          (existingItemMarketValue ?? 0) + this.quantity * this.effectivePrice;
      } else {
        if (!this.convertedProjectedItemMarketValue) return null;
        projectedLineMarketValueInPortfolioCurrency =
          this.convertedProjectedItemMarketValue;
      }

      // Pending Buy orders' buyCashAmountInPortfolioCurrency is already in
      // portfolio currency, so it's added after conversion to avoid converting it twice.

      const symbolName = this.currentSymbolName;

      if (!symbolName) {
        console.error(
          'Unable to compute projected line repartition: symbolName is null'
        );
        showErrorToast(this.$t('error_occured'));
        return null;
      }

      const pendingBuyValue =
        this.getPendingBuyOrdersValueForSymbol(symbolName);

      if (pendingBuyValue === null) {
        console.error(
          'Unable to compute projected line repartition: pendingBuyValue is null for symbol',
          symbolName
        );
        showErrorToast(this.$t('error_occured'));
        return null;
      }

      projectedLineMarketValueInPortfolioCurrency += pendingBuyValue;

      const operationType = this.formValues?.operationType;
      if (!operationType) {
        showErrorToast(this.$t('error_occured'));
        return null;
      }

      return projectedLineRepartitionPercent({
        isPublic,
        operationType,
        projectedLineMarketValueInPortfolioCurrency,
        portfolioMarketValue,
      });
    },
    projectedLineRepartitionPercentRounded() {
      const pct = this.projectedLineRepartitionPercent;
      return pct !== null ? projectedLineRepartitionPercentRounded(pct) : null;
    },
    exceedsThirtyPercentRule() {
      // In case of a SELL, we don't have to validate the 30% rule
      // On top of that, this was triggered really fast from the quantity slider, so better for performance to return immediately
      if (this.formValues.operationType == 'Sell') {
        return false;
      }
      const pct = this.projectedLineRepartitionPercent;
      return pct !== null ? exceedsThirtyPercentRule(pct) : null;
    },
    limitPriceNotValid() {
      return (
        this.selectedOrderType === this.orderTypes.LIMIT_PRICE &&
        (!this.orderLimitPrice || Number(this.orderLimitPrice) <= 0)
      );
    },
    effectivePrice() {
      return this.selectedOrderType === this.orderTypes.LIMIT_PRICE
        ? // If no limit is set, we use the current close to provide base behavior. Button remains disabled until a limit is set.
          this.orderLimitPrice ?? this.tickerToAdd?.close
        : this.price;
    },
    /**
     * Checks if an order is valid.
     * Purposefully treating the PAST_OPERATION as an order here as I think this is the way we'll want to go in the future
     * @returns {boolean}
     */
    isOrderValid() {
      return (
        // No validation for past operations
        this.selectedOrderType === PortfolioOrderTypes.PAST_OPERATION ||
        // No validation for limit price
        this.selectedOrderType === PortfolioOrderTypes.LIMIT_PRICE ||
        // Market price is valid if there is no pending market order of opposite type
        (this.selectedOrderType === PortfolioOrderTypes.MARKET_PRICE &&
          !this.hasPendingMarketOrderOfOppositeType())
      );
    },
    portfolioOrderTypes() {
      return PortfolioOrderTypes;
    },
    hasSelectedSymbol() {
      return this.createPortfolioOperationModal.symbol;
    },
    currentSymbolName() {
      // TODO - Depending on where we're coming from, sometimes this is name, and sometimes symbol. Quickfix here to support both, but this should be harmonized in a future refacto
      return (
        this.createPortfolioOperationModal?.symbol?.name ??
        this.createPortfolioOperationModal?.symbol?.symbol
      );
    },
  },
  watch: {
    conversionInputs: {
      deep: true,
      immediate: true,
      handler({ from, to, operationType }) {
        if (
          from &&
          to &&
          from !== to &&
          operationType === 'Buy' &&
          this.defaultPortfolio?.isPublic === true
        ) {
          const currenciesChanged =
            from !== this.tickerToPfCurrencyConversion.lastFrom ||
            to !== this.tickerToPfCurrencyConversion.lastTo;
          if (currenciesChanged) {
            this.tickerToPfCurrencyConversion.loading = true;
            this.debouncedFetchConversionRate(from, to);
          }
        } else {
          if (this.debouncedFetchConversionRate?.cancel) {
            this.debouncedFetchConversionRate.cancel();
          }
          this.tickerToPfCurrencyConversion.loading = false;
          this.tickerToPfCurrencyConversion.fetchedRate = null;
          this.tickerToPfCurrencyConversion.lastFrom = null;
          this.tickerToPfCurrencyConversion.lastTo = null;
        }
      },
    },
    quantity() {
      this.buttonState;
    },
    tickerToPfCurrencyConversionLoading() {
      this.buttonState;
    },
    price() {
      this.handlePriceUpdate();
    },
    priceLoading(newVal) {
      if (!newVal) this.mitigatePriceCallback();
    },
    orderLimitPrice() {
      this.handlePriceUpdate();
      this.buttonState;
    },
    'formValues.chosenCurrency'() {
      this.getConversionRate();

      this.updateQuantityOrTotal();

      this.$emit('chosenCurrency', this.getChosenCurrencyLabel());
    },
    total() {
      this.getCashAvailableText();
      const totalForDisplay = this.getRoundedTotalForDisplay();
      this.$emit('totalAmountChanged', totalForDisplay);
      if (this.defaultPortfolio) {
        this.buttonState;
        this.$emit('chosenCurrency', this.getChosenCurrencyLabel());
      }
    },
    tickerToAdd() {
      this.getConversionRate();
      if (this.tickerToAdd) {
        const date = this.$dayjs(this.tickerToAdd.date);
        const formattedDate = date.format('YYYY-MM-DD');
        if (this.formValues.date != formattedDate) {
          this.maxDate = this.formValues.date = formattedDate;
        }
        if (date.isAfter(this.maxDate)) {
          this.maxDate = formattedDate;
        }
      }
      this.buttonState;
    },
    'formValues.operationType'() {
      this.getCashAvailableText();
      this.$emit('onTransactionTypeChange', this.formValues.operationType);
    },
    shouldSubmit(newVal) {
      if (newVal === true) {
        this.submit(this.createPortfolioOperationModal.multipleTransactions);
        this.buttonState;
      }
    },
    defaultPortfolio() {
      this.getCashAvailableText();
      this.getDefaultChosenCurrency();
    },
    isOrderValid: {
      immediate: true,
      handler(isValid) {
        // Watching order validity separately from the button state to handle informative messages in the footer
        this.$emit('orderValidityChange', isValid);
      },
    },
    selectedOrderType() {
      // Triggering button validation event upon changing the orderType
      this.buttonState;
    },
  },
  created() {
    this.getDefaultChosenCurrency();
    this.getConversionRate();
    this.formValues.date = this.maxDate;
    this.formValues.operationType = this.defaultOperationType;
    this.mitigatePriceCallback();
    this.getCashAvailableText();
  },
  methods: {
    ...mapActions('modals', ['removeSymbolFromPortfolioOperation']),
    debouncedFetchConversionRate: debounce(function (from, to) {
      void this.fetchConversionRate(from, to);
    }, 400),
    async fetchConversionRate(from, to) {
      this.tickerToPfCurrencyConversion.loading = true;
      try {
        const today = getTodayAsDateString();
        const res = await this.$api.convertCurrency({
          amount: 1,
          from,
          to,
          date: today,
        });
        this.tickerToPfCurrencyConversion.fetchedRate = res.data;
        this.tickerToPfCurrencyConversion.lastFrom = from;
        this.tickerToPfCurrencyConversion.lastTo = to;
      } catch (error) {
        console.error('Error fetching conversion rate', error);
      } finally {
        this.tickerToPfCurrencyConversion.loading = false;
      }
    },
    updateFromQuantity(val) {
      // Special handling regarding cross-watchers initialization to keep the placeholder's behavior
      if (val === '' || val == null) {
        this.quantity = null;
        return;
      }
      // Don't convert to a Number here, it strips trailing zeros as you type

      if (this.formValues.operationType == 'Buy') {
        if (this.activeField !== 'total') {
          const q = val;
          const p = this.effectivePrice;
          const c = this.conversionRate;
          if (p > 0 && c > 0) {
            this.total = this.roundToDecimals(q * p * c, 6);
          } else {
            this.total = 0;
          }
        }
      } else if (this.formValues.operationType == 'Sell') {
        const isEditingQty = this.activeField === 'quantity';
        const isEditingPercentage = this.activeField === 'quantityPercentage';
        const isOverMax = !!val && val >= this.maxQuantityToSell;

        const qtyRoundOpts = {
          maxDecimals: _operationQuantityMaxDecimals,
          extraTinyDecimals: _operationQuantityMaxDecimals,
          mode: 'down',
          maxTinyDecimalsCap: 24,
        };

        const moneyRoundOpts = {
          maxDecimals: 4,
          extraTinyDecimals: 4,
          mode: 'down',
          maxTinyDecimalsCap: 24,
        };

        if (isOverMax) {
          if (isEditingQty) {
            this.quantity = this.roundForDisplay(
              this.maxQuantityToSell,
              qtyRoundOpts
            );
            this.quantityPercentage = 100;
          }

          const maxSellTotal =
            this.maxQuantityToSell * this.effectivePrice * this.conversionRate;

          this.total = this.roundForDisplay(maxSellTotal, moneyRoundOpts);
          return;
        }

        if (!isEditingPercentage) {
          const pct = (this.quantity / this.maxQuantityToSell) * 100;
          this.quantityPercentage = this.roundToDecimals(pct, 0);
        }
        this.total = this.roundForDisplay(
          this.quantity * this.effectivePrice * this.conversionRate,
          moneyRoundOpts
        );
      }
    },
    updateFromTotal(val) {
      // Special handling regarding cross-watchers initialization to keep the placeholder's behavior
      // Extra special here since that's not the active field by default
      if (val === '' || val == null || val === 0) {
        this.total = null;
        return;
      }
      // Same reason as above, don't convert to a Number here
      if (this.activeField !== 'quantity') {
        const p = this.effectivePrice;
        const c = this.conversionRate;

        if (p > 0 && c > 0) {
          const q = this.roundToDecimals(+val / (p * c), 4);
          this.quantity = q;
        } else {
          this.quantity = 0;
          this.total = 0;
        }
      }
    },
    submit(multipeTransactions) {
      // Regular stock transaction
      if (
        this.formValues.operationType == 'Sell' &&
        this.quantityPercentage == 100
      ) {
        this.quantity = this.maxQuantityToSell;
      }

      this.formValues.orderType = this.selectedOrderType;

      this.$emit(
        'onSubmit',
        {
          ...this.formValues,
        },
        multipeTransactions,
        (shouldResetState) => {
          this.error = '';
          this.total =
            this.effectivePrice * this.quantity * this.conversionRate;
          if (shouldResetState) {
            this.resetModalState();
          }

          if (multipeTransactions) {
            // In a special case where the line rule is broken for a ranked portfolio,
            // we must not reset the quantity. If the quantity were set to null and the user
            // chose to proceed with the transaction, the API call would fail due to a missing quantity value.
            if (shouldResetState) {
              this.quantity = null;
            }
            // Vue formulate doesn't provide api to reset form to pristine state
            // So this is just a trick to reset validation state on wanted fields
            // Unfortunately it does create a short "bumb" in the UI as the field disapears for a moment
            // So any better solution would be welcome
            this.reset = true;
            setTimeout(() => {
              this.reset = false;
            }, 1);
          }
        },
        false
      );
    },
    dateChange(context) {
      this.$emit('onDateChange', context.model, () =>
        this.mitigatePriceCallback()
      );
      this.getCashAvailableText();
      this.buttonState;
    },
    handleAddSymbol(symbol) {
      this.symbolToSell = symbol;
      this.$emit('onAddSymbol', symbol, () => this.mitigatePriceCallback());
    },
    mitigatePriceCallback() {
      // Adding to the monstrosity this fonction is, EODHD has numerous cases where high and low are the same
      // we want to handle such cases without shifting the price.
      // hopefully this is the last fix of the sort we have to make before work on decimal precision kicks in in the backend
      if (this.tickerToAdd && this.tickerToAdd.low == this.tickerToAdd.high) {
        this.formValues.price = this.tickerToAdd.high;
        return;
      }

      // Because of how float precision vary this has become hell
      // But basically this doubious bit is to handle cases chen close=low or close=high
      // And where conversions and approximations make the validation be incoherent
      // TODO : That only handle the default value and not the actual issue
      const basePrice = this.tickerToAdd ? this.tickerToAdd.close : 0;
      const shift = this.tickerToAdd
        ? this.tickerToAdd.close == this.tickerToAdd.low
          ? 0.02
          : this.tickerToAdd.close == this.tickerToAdd.high
          ? -0.02
          : 0
        : 0;
      const res = Math.floor((basePrice + shift) * 100) / 100;
      this.formValues.price = res;
    },
    resetModalState() {
      this.orderLimitPrice = null;
      this.quantity = null;
      this.total = null;
      // We empty the ticker only if the modal wasn't initially showed with the ticker card, indicating the search was performed, and will properly reset the state
      // Again, this is a quickfix as to not go too deep in refactoring the modal state management as of now, but could definitely be improved in a future refacto
      if (!this.createPortfolioOperationModal.shouldShowTickerCard) {
        // this.symbolToSell = null;
        if (this.operationType == 'Buy') {
          // Price doesn't need to be reset for a SELL, as we don't reset the ticker.
          this.price = null;
        }
        this.createPortfolioOperationModal.symbol = null;
        this.createPortfolioOperationModal.tickerValue = null;
      }
      this.maxDate = this.$dayjs()
        .subtract(this.daysToSubstract, 'day')
        .format('YYYY-MM-DD');
    },
    getDefaultChosenCurrency() {
      if (!this.defaultPortfolio) {
        this.formValues.chosenCurrency = this.currency;
        return;
      }
      if (this.currency in this.cashAvailable) {
        this.formValues.chosenCurrency = this.currency;
      } else {
        const keys = Object.keys(this.cashAvailable);
        if (keys.length > 0) {
          this.formValues.chosenCurrency = keys[0];
        }
      }
    },
    getOriginalCurrencyLabel() {
      if (this.tickerToAdd === null) {
        return '';
      }
      if (
        !(
          'currencySymbol' in this.tickerToAdd || 'currency' in this.tickerToAdd
        )
      ) {
        return '';
      }
      if (this.tickerToAdd.currencySymbol !== null) {
        return ' (' + this.tickerToAdd.currencySymbol + ')';
      }
      if (this.tickerToAdd.currency !== null) {
        return ' (' + this.tickerToAdd.currency + ')';
      }
      return '';
    },
    getChosenCurrencyLabel() {
      const currency = this.formValues.chosenCurrency;
      if (
        currency === null ||
        currency === undefined ||
        currency.length !== 3
      ) {
        return '';
      }
      const symbol = this.$display.money.getCurrencySymbol(currency);
      if (symbol !== '') {
        return symbol;
      }
      return currency;
    },
    getConversionRate() {
      if (!this.tickerToAdd || !this.formValues.chosenCurrency) {
        this.conversionRate = 1;
        return;
      }
      if (
        this.tickerToAdd.currency === this.formValues.chosenCurrency ||
        !('conversions' in this.tickerToAdd || 'conversion' in this.tickerToAdd)
      ) {
        this.conversionRate = 1;
      } else {
        if (
          this.tickerToAdd.conversions !== null &&
          this.tickerToAdd.conversions.length > 0
        ) {
          const conversionObject = this.tickerToAdd.conversions.find(
            (c) => c.currency === this.formValues.chosenCurrency
          );
          this.conversionRate =
            this.defaultOperationType == 'Sell' ? 1 : conversionObject.rate;
        } else if (
          this.tickerToAdd.conversion !== null &&
          this.tickerToAdd.conversion.currency ===
            this.formValues.chosenCurrency
        ) {
          this.conversionRate =
            this.defaultOperationType == 'Sell'
              ? 1
              : this.tickerToAdd.conversion.rate;
        } else {
          this.conversionRate = 1;
        }
      }
    },
    /**
     * Calculate the options for cash available, with text (symbol + conversions)
     */
    getCashAvailableText() {
      const toReturn = {};
      const chosenCurrency = this.formValues.chosenCurrency;
      let text;
      let defaultCurrencySymbol = this.$display.money.getCurrencySymbol(
        this.currency
      );
      if (defaultCurrencySymbol === '') {
        defaultCurrencySymbol = this.currency;
      }
      for (const [key, value] of Object.entries(this.cashAvailable)) {
        text = this.$display.money.money(
          value.value,
          value.symbol ?? 'USD',
          0,
          2
        );

        // If not enough cash
        if (
          this.formValues.operationType === 'Buy' &&
          chosenCurrency === key &&
          value.value < this.total &&
          // NOTE - SKD-2310 - This is now disabled for all portfolios. Visit cashOperationsPermitted() and remove comment if that changes
          this.cashOperationsPermitted
        ) {
          text +=
            ' (' +
            this.$t('cash_will_be_added', {
              amount: this.$display.money.money(
                this.total - value.value,
                value.symbol ?? 'USD',
                0,
                2
              ),
            }) +
            ')';
        }
        toReturn[key] = text;
      }
      this.cashAvailableText = toReturn;
    },
    withinPriceRange() {
      // For portfolios in league, price isn't editable, so we skip price validation
      if (this.defaultPortfolio.leagueId) return true;

      // Individual price written outside range
      const eps = Number.EPSILON;

      if (this.price < this.min - eps || this.max + eps < this.price) {
        return false;
      }

      // Obsolete code, kept for reference
      // Total cost outside range (using EPSILON to avoid rounding errors)
      // const totalMin = this.min * this.quantity * this.conversionRate;
      // const totalMax = this.max * this.quantity * this.conversionRate;

      // if (this.total < totalMin - eps || totalMax + eps < this.total) {
      //   return false;
      // }

      return true;
    },
    round(num) {
      return Math.trunc(num * 100) / 100;
    },
    getPriceError() {
      // For portfolios in league, price isn't editable, so we skip price validation
      if (this.defaultPortfolio.leagueId) return '';
      if (!this.withinPriceRange()) {
        return this.$t('price_between', {
          min: this.$display.money.money(
            this.min,
            this.originalCurrency,
            2,
            2,
            false
          ),
          max: this.$display.money.money(
            this.max,
            this.originalCurrency,
            2,
            2,
            false
          ),
        });
      }
    },
    getPriceHelp() {
      if (this.selectedOrderType !== PortfolioOrderTypes.PAST_OPERATION) {
        return '';
      }
      // TODO - Could simplify this by properly initializing min and max to null and handle this properly in other helpers
      if (!this.min && !this.max) return '';
      if (!this.withinPriceRange()) {
        return '';
      }
      return this.$t('price_between', {
        min: this.$display.money.money(
          this.min,
          this.originalCurrency,
          2,
          2,
          false
        ),
        max: this.$display.money.money(
          this.max,
          this.originalCurrency,
          2,
          2,
          false
        ),
      });
    },
    getQuantityHelp() {
      return this.$t('asset_quantity_available', {
        quantity: this.$display.number.number(
          this.maxQuantityToSell,
          0,
          _operationQuantityMaxDecimals
        ),
      });
    },
    handleSymbolRemove() {
      this.$emit('onSymbolRemove');
      this.resetModalState();
      this.focusTickerSearchField = true;
    },
    getOperationButtonClass(operationType) {
      if (this.formValues.operationType == operationType) {
        return 'bg-primary-light text-primary';
      } else {
        return 'text-neutral-7';
      }
    },
    handleOperationSwitch(operation) {
      if (operation === 'Buy') {
        this.formValues.operationType = 'Buy';
        this.resetModalState();
        this.removeSymbolFromPortfolioOperation();
      } else if (operation === 'Sell') {
        this.formValues.operationType = 'Sell';
        this.resetModalState();
        this.removeSymbolFromPortfolioOperation();
      }
    },
    handlePortfolioChange(portfolioName) {
      this.$emit('onPortfolioChange', portfolioName, () =>
        this.mitigatePriceCallback()
      );
    },
    getRoundedTotalForDisplay() {
      const rounded = Math.round(this.total * 100) / 100;
      // SKD-1516: Ensure positive totals less than 0.01 are not displayed as 0.00
      return this.total > 0 && rounded === 0 ? 0.01 : +rounded.toFixed(2);
    },
    roundToDecimals(value, decimals = 6) {
      if (value == null || isNaN(value)) return 0;
      const factor = 10 ** decimals;
      return Math.round((+value + Number.EPSILON) * factor) / factor;
    },
    /**
     * Tries to respect the number of decimals given, but goes up to the first significant number if the number is too small
     *  Rules:
     * - Prefer maxDecimals (default 4) if that doesn't become 0.
     * - If it becomes 0, show up to (first significant digit + extraTinyDecimals) decimals.
     * - If mode is "down", never round up (prevents exceeding available qty at 100%).
     *
     * @param {number|string} value
     * @param {object} opts
     * @param {number} opts.maxDecimals - normal rounding decimals (default 4)
     * @param {number} opts.extraTinyDecimals - extra decimals after first significant digit (default 4)
     * @param {"nearest"|"down"} opts.mode - rounding mode (default "nearest")
     * @param {number} opts.maxTinyDecimalsCap - safety cap (default 18)
     */
    roundForDisplay(value, opts = {}) {
      // Backward compatible: allow roundForDisplay(x, 4)
      if (typeof opts === 'number') opts = { maxDecimals: opts };

      const {
        maxDecimals = 4,
        extraTinyDecimals = 4,
        mode = 'nearest', // "nearest" | "down"
        maxTinyDecimalsCap = 24,
      } = opts;

      if (value == null || value === '' || Number.isNaN(Number(value)))
        return 0;

      const n = Number(value);
      if (n === 0) return 0;

      const apply = (num, decimals) => {
        const factor = 10 ** decimals;

        if (mode === 'down') {
          // NO EPSILON here; also clamp so we never exceed original num
          const floored = Math.floor(num * factor) / factor;
          return num > 0 ? Math.min(floored, num) : Math.max(floored, num);
        }

        // nearest
        return Math.round((num + Number.EPSILON) * factor) / factor;
      };

      // 1) Try normal rounding first (maxDecimals)
      const rounded = apply(n, maxDecimals);
      if (rounded !== 0) return rounded;

      // 2) Too small -> first significant digit + extraTinyDecimals
      const abs = Math.abs(n);
      const decimalsToFirstSig = Math.ceil(-Math.log10(abs));
      const decimals = Math.min(
        decimalsToFirstSig + extraTinyDecimals,
        maxTinyDecimalsCap
      );

      return apply(n, decimals);
    },
    updateFromQuantityPercentage(value) {
      this.activeField = 'quantityPercentage';
      this.quantityPercentage = value;
      if (value == 100) {
        this.quantity = this.roundForDisplay(this.maxQuantityToSell, {
          maxDecimals: _operationQuantityMaxDecimals,
          extraTinyDecimals: _operationQuantityMaxDecimals, // “first significant + 4”
          mode: 'down',
          maxTinyDecimalsCap: 24,
        });
        return;
      }
      this.quantity = this.roundForDisplay(
        (this.maxQuantityToSell * value) / 100,
        {
          maxDecimals: _operationQuantityMaxDecimals,
          extraTinyDecimals: _operationQuantityMaxDecimals, // “first significant + 4”
          mode: 'down',
          maxTinyDecimalsCap: 24,
        }
      );
    },
    handleSelectOrderType(type) {
      this.selectedOrderType = type;
      this.$emit('selectOrderType', type);
    },
    // Pending/ready Buy orders on the item add to its potential market value
    // if they all go through, so they must count towards the 30% rule.
    // symbolName must have a value
    getPendingBuyOrdersValueForSymbol(symbolName) {
      if (!this.defaultPortfolio?.orders) return null;

      let sum = 0;
      for (const order of this.defaultPortfolio.orders) {
        if (
          order.operationType !== 'Buy' ||
          !isOrderPending(order.orderState) ||
          order.stock?.name !== symbolName
        ) {
          continue;
        }

        if (order.buyCashAmountInPortfolioCurrency == null) {
          console.error(
            'Unexpected null buyCashAmountInPortfolioCurrency on order',
            order
          );
          showErrorToast(this.$t('error_occured'));
          return null;
        }

        sum += order.buyCashAmountInPortfolioCurrency;
      }

      return sum;
    },
    handlePriceUpdate() {
      this.updateQuantityOrTotal();
    },
    /**
     * Checks if there is a pending market order of opposite type for MARKET_PRICE orders validation
     * @returns {boolean}
     */
    hasPendingMarketOrderOfOppositeType() {
      if (!this.defaultPortfolio?.orders) return false;

      // Validating symbolName to avoid potential errors with undefined or null symbol
      // Again, taking both name and symbol to match different modal states
      const symbolName =
        this.createPortfolioOperationModal?.symbol?.name ??
        this.createPortfolioOperationModal?.symbol?.symbol;
      if (!symbolName) return false;

      const oppositeOperationType =
        this.formValues.operationType === 'Buy'
          ? 'Sell'
          : this.formValues.operationType === 'Sell'
          ? 'Buy'
          : null;

      if (!oppositeOperationType) return false;

      return this.defaultPortfolio.orders.some(
        (order) =>
          order.orderType === PortfolioOrderTypes.MARKET_PRICE &&
          isOrderPending(order.orderState) &&
          order.operationType === oppositeOperationType &&
          order.stock?.name === symbolName
      );
    },
    updateQuantityOrTotal() {
      if (this.activeField == 'quantity')
        this.updateFromQuantity(this.quantity);
      else if (this.activeField == 'total') this.updateFromTotal(this.total);
    },
  },
};
